// src/sections/notifications/NotificationPanel.jsx
// -----------------------------------------------------------------
// iOS frosted glass notification panel — popover (desktop) / drawer
// (mobile) from the bell icon. Tabs: All | Action | Updates
//
// Glass aesthetic matching NotificationCard / NotificationList:
// - Frosted glass header with backdrop-filter blur
// - Glass tab bar with pill-style active indicator
// - Glass empty state
// - Priority sections in All tab (urgent → normal → low)
// - Passes new props to glass NotificationCard (onMarkRead, snoozedUntil, animDelay)
// -----------------------------------------------------------------

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Badge,
  IconButton,
  Popover,
  Drawer,
  Switch,
  FormControlLabel,
  CircularProgress,
  styled,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import NotificationCard from './NotificationCard';
import { NOTIFICATION_PALETTE } from './notificationTypes';

// localStorage key for the "Unread only" toggle. Survives reload/restart
// so the user's preference is sticky between sessions, matching how
// Gmail and most inbox UIs treat the filter pill.
const UNREAD_ONLY_STORAGE_KEY = 'notifPanel.unreadOnly';
// Sticky category filter — same idea, so jumping between sessions doesn't
// silently reset whatever facet the user pinned ("just show me Payments").
const CATEGORY_STORAGE_KEY = 'notifPanel.category';

// ── Constants ───────────────────────────────────────────────────

const TABS = [
  { key: 'all', label: 'All', filter: null },
  { key: 'action', label: 'Action', filter: (n) => n.requires_action !== false },
  { key: 'updates', label: 'Updates', filter: (n) => n.requires_action === false },
];

// Lightweight category facets layered on top of the existing tier/type
// taxonomy in notificationTypes.js. Kept deliberately small — five buckets
// the user already speaks in (leads / tasks / payments / treatments /
// comms) plus an explicit "All". Anything outside the mapping (e.g.
// LEAD_FOLLOW_UP_MOVED → "leads") falls through to its closest match.
// We match on the normalised `type` key the card already carries, so
// adding a new verb only requires extending VERB_TO_TYPE upstream.
const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'leads', label: 'Leads', types: [
    'NEW_LEAD', 'LEAD_ASSIGNED', 'LEAD_UNASSIGNED', 'LEAD_RESTORED',
    'LEAD_FOLLOW_UP_MOVED', 'CALLBACK_REQUEST',
  ] },
  { key: 'tasks', label: 'Tasks', types: [
    'TASK_OVERDUE', 'TASK_DUE_TODAY', 'TASK_ASSIGNED',
  ] },
  { key: 'comms', label: 'Comms', types: [
    'WHATSAPP_MESSAGE', 'EMAIL_REPLY', 'FACEBOOK_COMMENT', 'PHONE_CLICKED',
    'WHATSAPP_BUTTON_CLICKED', 'CALL_RINGING', 'CALL_MISSED', 'CALL_ANSWERED',
  ] },
  { key: 'payments', label: 'Payments', types: [
    'DEPOSIT_PAID', 'DEPOSIT_NOT_PAID',
  ] },
  { key: 'treatments', label: 'Treatments', types: [
    'TREATMENT_STARTED',
  ] },
];

// Cap the visible badge number to keep the pill from ballooning when the
// user has hundreds unread. Capped at 25 by product call — the bell isn't
// a counter, it's a hint to come look.
const BADGE_CAP = 25;

const PANEL_WIDTH = 420;
const PANEL_MAX_HEIGHT = 640;

// ── Animations ──────────────────────────────────────────────────

const panelSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// ── Styled components ───────────────────────────────────────────

const PanelRoot = styled(Box)(() => ({
  width: PANEL_WIDTH,
  maxHeight: PANEL_MAX_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'rgba(240, 242, 245, 0.65)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  animation: `${panelSlideIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)`,
}));

const PanelHeader = styled(Box)(() => ({
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 16px 12px',
  borderBottom: '0.5px solid rgba(0,0,0,0.06)',
}));

const UnreadPill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 22,
  height: 22,
  padding: '0 6px',
  borderRadius: 11,
  fontSize: 11,
  fontWeight: 700,
  backgroundColor: theme.palette.error.main,
  color: '#fff',
  marginLeft: 8,
}));

const GlassHeaderButton = styled('button')(() => ({
  fontFamily: "'Public Sans', sans-serif",
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 9,
  background: 'rgba(255,255,255,0.6)',
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  color: '#637381',
  transition: 'all 0.15s',
  '&:hover': {
    background: 'rgba(255,255,255,0.85)',
    color: '#212B36',
  },
}));

const TabBar = styled(Box)(() => ({
  display: 'flex',
  flexShrink: 0,
  gap: 4,
  padding: '8px 16px',
  borderBottom: '0.5px solid rgba(0,0,0,0.06)',
}));

// Compact category facet row. Horizontal-scrollable so adding a future
// bucket doesn't blow the panel width. Deliberately quiet visually —
// these are a secondary filter, not the primary one.
//
// flexShrink: 0 is load-bearing: `overflowX: 'auto'` collapses this row's
// implicit `min-height: auto` to 0, which lets the column-flex parent
// (PanelRoot) squash it whenever sibling content competes for space. The
// visible symptom was chip labels clipped vertically into thin slivers.
const CategoryRow = styled(Box)(() => ({
  display: 'flex',
  flexShrink: 0,
  gap: 6,
  padding: '6px 16px 4px',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
}));

const CategoryChip = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  fontFamily: "'Public Sans', sans-serif",
  flexShrink: 0,
  border: 'none',
  cursor: 'pointer',
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: active ? 700 : 600,
  letterSpacing: '-0.1px',
  color: active ? '#fff' : '#637381',
  background: active ? '#212B36' : 'rgba(255,255,255,0.6)',
  boxShadow: active ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
  transition: 'all 0.15s ease',
  '&:hover': {
    ...(active ? {} : { background: 'rgba(255,255,255,0.85)', color: '#212B36' }),
  },
}));

const TabButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  fontFamily: "'Public Sans', sans-serif",
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '6px 12px',
  borderRadius: 9,
  border: 'none',
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  color: active ? '#212B36' : '#919EAB',
  backgroundColor: active ? 'rgba(255,255,255,0.85)' : 'transparent',
  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  letterSpacing: '-0.1px',
  '&:hover': {
    ...(!active && { backgroundColor: 'rgba(255,255,255,0.4)' }),
  },
}));

const CountBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  borderRadius: 8,
  fontSize: 10,
  fontWeight: 700,
  backgroundColor: active ? '#212B36' : NOTIFICATION_PALETTE.grey[300],
  color: active ? '#fff' : '#919EAB',
}));

const ScrollArea = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  padding: '8px 12px 12px',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(145,158,171,0.2)',
    borderRadius: 2,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
}));

const SectionLabel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '14px 4px 6px',
}));

const SectionIcon = styled(Box)(() => ({
  width: 20,
  height: 20,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const EmptyState = styled(Box)(() => ({
  textAlign: 'center',
  padding: '48px 16px',
  color: '#919EAB',
}));

const EmptyIcon = styled(Box)(() => ({
  width: 52,
  height: 52,
  borderRadius: 14,
  margin: '0 auto 12px',
  background: 'rgba(255,255,255,0.6)',
  backdropFilter: 'blur(20px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#C4CDD5',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
}));

const PanelFooter = styled('button')(() => ({
  fontFamily: "'Public Sans', sans-serif",
  flexShrink: 0,
  borderTop: '0.5px solid rgba(0,0,0,0.06)',
  padding: '12px 16px',
  border: 'none',
  borderTopStyle: 'solid',
  borderTopWidth: '0.5px',
  borderTopColor: 'rgba(0,0,0,0.06)',
  background: 'transparent',
  width: '100%',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
  color: NOTIFICATION_PALETTE.primary.main,
  letterSpacing: '-0.1px',
  transition: 'background 0.15s',
  '&:hover': {
    background: 'rgba(0,0,0,0.02)',
  },
}));

// ── Empty state messages per tab ────────────────────────────────

const EMPTY_MESSAGES = {
  all: 'No new notifications',
  action: 'No action items right now',
  updates: 'No updates yet',
};

// ── Shared card renderer ────────────────────────────────────────

function renderNotificationCard(notification, index, { handleAction, handleDismiss, handleMarkRead, onSnooze }) {
  return (
    <NotificationCard
      key={notification.id}
      type={notification.type}
      message={notification.message}
      timeAgo={notification.timeAgo}
      preview={notification.preview}
      chips={notification.chips}
      practiceName={notification.practiceName}
      starRating={notification.starRating}
      isRead={notification.isRead}
      priority={notification.priority}
      actionLabel={notification.action_label}
      actions={notification.callActions}
      snoozable={notification.snoozable}
      snoozedUntil={notification.snoozed_until}
      groupedCount={notification.grouped_count}
      timestamp={notification.timestamp}
      animDelay={index * 0.04}
      onAction={(actionLbl) => handleAction(notification.id, actionLbl)}
      onDismiss={() => handleDismiss(notification.id)}
      onMarkRead={handleMarkRead ? () => handleMarkRead(notification.id) : undefined}
      onSnooze={onSnooze ? (minutes) => onSnooze(notification.id, minutes) : undefined}
    />
  );
}

// ── Render section header ────────────────────────────────────────

function renderSectionHeader(label, count, icon, colour) {
  return (
    <SectionLabel key={`section-${label}`}>
      <SectionIcon sx={{ bgcolor: colour?.lighter || NOTIFICATION_PALETTE.grey[200], color: colour?.dark || NOTIFICATION_PALETTE.grey[600] }}>
        <Iconify icon={icon} width={11} />
      </SectionIcon>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.3px', textTransform: 'uppercase', color: '#919EAB' }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: 10, fontWeight: 700, color: '#919EAB', bgcolor: NOTIFICATION_PALETTE.grey[200], px: '6px', py: '1px', borderRadius: '5px' }}>
        {count}
      </Box>
    </SectionLabel>
  );
}

// -----------------------------------------------------------------
// NotificationBellButton
// -----------------------------------------------------------------

export function NotificationBellButton({ unreadCount, onClick, ...other }) {
  return (
    <IconButton onClick={onClick} {...other}>
      <Badge
        badgeContent={unreadCount}
        color="error"
        // Cap at BADGE_CAP — see notes at the constant. Matches the
        // truncation used by the "Notifications X+" pill in the header.
        max={BADGE_CAP}
        invisible={!unreadCount || unreadCount <= 0}
      >
        <Iconify icon="solar:bell-bing-bold-duotone" width={24} />
      </Badge>
    </IconButton>
  );
}

NotificationBellButton.propTypes = {
  unreadCount: PropTypes.number,
  onClick: PropTypes.func,
};

// -----------------------------------------------------------------
// NotificationPanel
// -----------------------------------------------------------------

export default function NotificationPanel({
  notifications = [],
  // True totals from redux (NOT capped by the page-size of the list fetch).
  // When provided we render these in the bell badge + Notifications pill;
  // otherwise we fall back to deriving them locally from `notifications`,
  // which is what the legacy callers (e.g. example/storybook usage) expect.
  trueUnreadCount,
  totalCount,
  // Infinite scroll plumbing — opt-in. When all three are provided, the
  // panel hooks an IntersectionObserver to a sentinel at the bottom of
  // the scroll area and calls onLoadMore when it comes into view.
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onAction,
  onDismiss,
  onMarkRead,
  onMarkAllRead,
  onViewAll,
  onSnooze,
  onOpenSettings,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const anchorRef = useRef(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  // Persist Unread-only across sessions (Gmail-style sticky filter).
  // localStorage read is wrapped in try/catch because some tested
  // environments (private windows, SSR) make it throw.
  const [unreadOnly, setUnreadOnly] = useState(() => {
    try { return window.localStorage.getItem(UNREAD_ONLY_STORAGE_KEY) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(UNREAD_ONLY_STORAGE_KEY, unreadOnly ? '1' : '0'); }
    catch { /* swallow — sticky filter is best-effort */ }
  }, [unreadOnly]);

  // Category facet — defaults to 'all', persisted alongside unreadOnly so
  // the panel reopens to whatever the user was working through.
  const [activeCategory, setActiveCategory] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CATEGORY_STORAGE_KEY);
      return CATEGORIES.some((c) => c.key === raw) ? raw : 'all';
    } catch { return 'all'; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(CATEGORY_STORAGE_KEY, activeCategory); }
    catch { /* swallow — sticky filter is best-effort */ }
  }, [activeCategory]);

  // Locally-derived unread count over what's loaded. Used only as a fallback
  // when the caller doesn't pass trueUnreadCount (legacy / examples).
  const localUnreadCount = useMemo(
    () => notifications.filter((n) => !n.isRead).length,
    [notifications]
  );
  // The authoritative count for everything user-facing in the header.
  const headerUnreadCount = (typeof trueUnreadCount === 'number') ? trueUnreadCount : localUnreadCount;

  // Apply Unread-only first, THEN category, THEN per-tab filter. Order
  // matters because the tab counts should reflect the unread + category
  // filters when they're on (otherwise the "Action 10" badge would be a
  // lie when "Unread only" or a category facet is hiding rows below).
  const categoryTypeSet = useMemo(() => {
    const cat = CATEGORIES.find((c) => c.key === activeCategory);
    if (!cat || !cat.types) return null; // null = "All"
    return new Set(cat.types);
  }, [activeCategory]);

  const baseNotifications = useMemo(() => {
    let out = notifications;
    if (unreadOnly) out = out.filter((n) => !n.isRead);
    if (categoryTypeSet) {
      out = out.filter((n) => n.type && categoryTypeSet.has(n.type));
    }
    return out;
  }, [notifications, unreadOnly, categoryTypeSet]);

  // Filter by active tab
  const filteredNotifications = useMemo(() => {
    const tab = TABS.find((t) => t.key === activeTab);
    if (!tab?.filter) return baseNotifications;
    return baseNotifications.filter(tab.filter);
  }, [baseNotifications, activeTab]);

  // Client-side grouping: collapse runs of same-verb-same-actor
  // notifications into a single card with a count, like Gmail collapses
  // thread replies. Mutates a SHALLOW copy of the surviving notification
  // to add a synthetic `grouped_count` so NotificationCard renders the
  // pill. The BE already does first-party grouping for telemetry verbs
  // (phone_number_clicked, whatsapp_button_clicked) — this is purely a
  // visual layer for the rest. Disabled when filters reduce the list to
  // a small enough size that grouping wouldn't help.
  const groupedForDisplay = useMemo(() => {
    if (filteredNotifications.length < 3) return filteredNotifications;
    const out = [];
    let runStart = null;
    let runCount = 0;
    // Call rows are one per call and carry their own actions: never collapse them.
    const keyOf = (n) => (n.callState ? `call::${n.id}` : `${n.type || n._raw?.verb || ''}::${n.practiceName || ''}::${n.message || ''}`);
    const flushRun = () => {
      if (!runStart) return;
      if (runCount > 1) {
        const incomingServerGrouped = runStart.grouped_count && runStart.grouped_count > 1 ? runStart.grouped_count : 0;
        out.push({
          ...runStart,
          // If the BE already set a grouped_count (telemetry case), respect
          // it and add our client-side dupes on top. Otherwise just use the
          // client run length.
          grouped_count: incomingServerGrouped ? incomingServerGrouped + (runCount - 1) : runCount,
        });
      } else {
        out.push(runStart);
      }
    };
    filteredNotifications.forEach((n) => {
      if (runStart && keyOf(runStart) === keyOf(n)) {
        runCount += 1;
      } else {
        flushRun();
        runStart = n;
        runCount = 1;
      }
    });
    flushRun();
    return out;
  }, [filteredNotifications]);

  // Priority-section rendering was removed from the All tab. With a wide
  // taxonomy of low-priority telemetry verbs (whatsapp_button_clicked,
  // phone_number_clicked, gmb_review_created…) the "Low priority" group
  // ended up below 40+ normal-priority rows on a busy inbox, which meant a
  // freshly-arrived low-priority notification was invisible until the
  // user scrolled to the bottom — even though "All" claimed to show
  // everything. Priority is still encoded on each card (accent colour,
  // pulse, snooze affordance), so dropping the sectioning keeps the
  // information without burying recent rows. Action / Updates tabs are
  // unchanged.
  const urgent = useMemo(() => groupedForDisplay.filter((n) => n.priority === 'urgent'), [groupedForDisplay]);

  // Count per tab. When Unread-only is on, these reflect filtered counts;
  // otherwise they reflect the loaded slice. We deliberately do NOT use
  // totalCount here because we don't have per-tab BE counts — being honest
  // about "of what's loaded" is better than guessing.
  const tabCounts = useMemo(() => ({
    all: baseNotifications.length,
    action: baseNotifications.filter((n) => n.requires_action !== false).length,
    updates: baseNotifications.filter((n) => n.requires_action === false).length,
  }), [baseNotifications]);

  // Infinite scroll: watch a sentinel at the bottom of the list. When it
  // enters the scroll area we trigger onLoadMore. IntersectionObserver is
  // cheaper than a scroll-handler and avoids fighting React rendering.
  useEffect(() => {
    if (!onLoadMore || !hasMore || !open) return undefined;
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          onLoadMore();
        }
      },
      { root, rootMargin: '120px 0px', threshold: 0 },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [onLoadMore, hasMore, open, groupedForDisplay.length]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleAction = useCallback((id, actionLabel) => {
    onAction?.(id, actionLabel);
    handleClose();
  }, [onAction, handleClose]);

  const handleDismiss = useCallback((id) => {
    onDismiss?.(id);
  }, [onDismiss]);

  const handleMarkRead = useMemo(
    () => (onMarkRead ? (id) => onMarkRead(id) : null),
    [onMarkRead]
  );

  const handleMarkAllRead = useCallback(() => {
    onMarkAllRead?.();
  }, [onMarkAllRead]);

  const handleViewAll = useCallback(() => {
    onViewAll?.();
    handleClose();
  }, [onViewAll, handleClose]);

  const handleOpenSettings = useCallback(() => {
    onOpenSettings?.();
    handleClose();
  }, [onOpenSettings, handleClose]);

  // Card-render helpers
  const cardHandlers = useMemo(
    () => ({ handleAction, handleDismiss, handleMarkRead, onSnooze }),
    [handleAction, handleDismiss, handleMarkRead, onSnooze]
  );

  // All tab now renders flat-chronological (newest first) — the backend
  // already orders results that way, and grouping/dedup is handled by
  // groupedForDisplay above. Urgent rows still keep their "Action needed"
  // call-out for affordance, but normal/low are interleaved by recency so
  // a fresh whatsapp_button_clicked doesn't get buried under 40 lead rows.
  const renderAllFlat = () => {
    const hasUrgentHeader = urgent.length > 0 && urgent.length < groupedForDisplay.length;
    if (!hasUrgentHeader) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {groupedForDisplay.map((n, i) => renderNotificationCard(n, i, cardHandlers))}
        </Box>
      );
    }
    // Mixed list with a single urgent header at the top. Cards keep their
    // original ordering within each bucket — we don't re-sort by timestamp
    // here because the BE ordering (newest-first) is already what we want.
    const others = groupedForDisplay.filter((n) => n.priority !== 'urgent');
    let idx = 0;
    return (
      <>
        {renderSectionHeader('Action needed', urgent.length, 'solar:danger-triangle-bold-duotone', NOTIFICATION_PALETTE.error)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {urgent.map((n) => {
            const card = renderNotificationCard(n, idx, cardHandlers);
            idx += 1;
            return card;
          })}
        </Box>
        {renderSectionHeader('Recent', others.length, 'solar:letter-bold-duotone', NOTIFICATION_PALETTE.info)}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {others.map((n) => {
            const card = renderNotificationCard(n, idx, cardHandlers);
            idx += 1;
            return card;
          })}
        </Box>
      </>
    );
  };

  // ── Panel content ────────────────────────────────────────────

  // Cap the visible badge number — see BADGE_CAP. Anything above renders
  // as "<cap>+" so the pill doesn't balloon on a busy inbox.
  const formatPillCount = (n) => (n > BADGE_CAP ? `${BADGE_CAP}+` : `${n}`);

  const panelContent = (
    <PanelRoot>
      {/* Header */}
      <PanelHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.3px' }}>
            Notifications
          </Typography>
          {headerUnreadCount > 0 && <UnreadPill>{formatPillCount(headerUnreadCount)}</UnreadPill>}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <GlassHeaderButton onClick={handleOpenSettings} title="Notification settings">
            <Iconify icon="solar:settings-bold-duotone" width={16} />
          </GlassHeaderButton>
        </Box>
      </PanelHeader>

      {/* Tab filters */}
      <TabBar>
        {TABS.map((tab) => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key ? 1 : 0}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tabCounts[tab.key] > 0 && (
              <CountBadge active={activeTab === tab.key ? 1 : 0}>
                {tabCounts[tab.key]}
              </CountBadge>
            )}
          </TabButton>
        ))}
      </TabBar>

      {/* Category facets — hidden when there's nothing loaded yet so the
          empty state isn't fronted by an inert row of pills. Stays sticky
          across sessions via localStorage so the user's pinned facet
          doesn't reset every time the panel closes. */}
      {notifications.length > 0 && (
        <CategoryRow>
          {CATEGORIES.map((cat) => (
            <CategoryChip
              key={cat.key}
              type="button"
              active={activeCategory === cat.key ? 1 : 0}
              onClick={() => setActiveCategory(cat.key)}
            >
              {cat.label}
            </CategoryChip>
          ))}
        </CategoryRow>
      )}

      {/* Toolbar: Mark all read + Unread-only toggle. Sits between the
          tabs and the list so the affordances are always above the fold.
          Hidden when there are zero notifications loaded — the empty
          state below renders its own message. */}
      {notifications.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 16px 8px',
            borderBottom: '0.5px solid rgba(0,0,0,0.06)',
          }}
        >
          <FormControlLabel
            control={(
              <Switch
                size="small"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
              />
            )}
            label={(
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                Unread only
              </Typography>
            )}
            sx={{ ml: 0, mr: 0 }}
          />
          {headerUnreadCount > 0 && onMarkAllRead && (
            <Box
              component="button"
              type="button"
              onClick={handleMarkAllRead}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                border: 'none',
                background: 'transparent',
                fontFamily: "'Public Sans', sans-serif",
                fontSize: 12,
                fontWeight: 700,
                color: NOTIFICATION_PALETTE.primary.main,
                cursor: 'pointer',
                borderRadius: 8,
                '&:hover': { background: 'rgba(0,0,0,0.04)' },
              }}
            >
              <Iconify icon="solar:check-read-bold" width={14} />
              Mark all as read
            </Box>
          )}
        </Box>
      )}

      {/* Scrollable card list */}
      <ScrollArea ref={scrollRef}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {filteredNotifications.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <Iconify icon="solar:check-read-bold" width={24} />
            </EmptyIcon>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary', mb: '4px' }}>
              You&apos;re all caught up
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
              {unreadOnly ? 'No unread notifications' : EMPTY_MESSAGES[activeTab]}
            </Typography>
          </EmptyState>
        ) : activeTab === 'all' ? (
          // All tab: flat chronological with optional urgent header
          renderAllFlat()
        ) : (
          // Flat list for Action / Updates tabs
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {groupedForDisplay.map((n, i) => renderNotificationCard(n, i, cardHandlers))}
          </Box>
        )}

        {/* Infinite-scroll sentinel + spinner. Only rendered when we have
            more to fetch — otherwise the IntersectionObserver effect is
            a no-op anyway. */}
        {hasMore && filteredNotifications.length > 0 && (
          <Box
            ref={sentinelRef}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1.5, minHeight: 36 }}
          >
            {isLoadingMore ? (
              <CircularProgress size={18} thickness={5} />
            ) : (
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
                Scroll for more
              </Typography>
            )}
          </Box>
        )}
      </ScrollArea>

      {/* Footer */}
      {notifications.length > 0 && (
        <PanelFooter onClick={handleViewAll}>
          View All Notifications
          {typeof totalCount === 'number' && totalCount > notifications.length
            ? ` (${totalCount})`
            : ''}
        </PanelFooter>
      )}
    </PanelRoot>
  );

  return (
    <>
      {/* Bell button — use the true unread count (redux-sourced) so the
          badge isn't capped by the bell list's page size. */}
      <NotificationBellButton
        ref={anchorRef}
        unreadCount={headerUnreadCount}
        onClick={handleOpen}
      />

      {/* Desktop: Popover */}
      {!isMobile && (
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: '18px',
                boxShadow: '0 20px 40px -4px rgba(145,158,171,0.24), 0 0 2px 0 rgba(145,158,171,0.24)',
                overflow: 'hidden',
                border: '0.5px solid rgba(255,255,255,0.4)',
              },
            },
          }}
        >
          {panelContent}
        </Popover>
      )}

      {/* Mobile: Drawer */}
      {isMobile && (
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: PANEL_WIDTH,
              background: 'rgba(240, 242, 245, 0.95)',
              backdropFilter: 'blur(20px)',
            },
          }}
        >
          {panelContent}
        </Drawer>
      )}
    </>
  );
}

NotificationPanel.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      timeAgo: PropTypes.string,
      preview: PropTypes.string,
      chips: PropTypes.array,
      practiceName: PropTypes.string,
      starRating: PropTypes.number,
      isRead: PropTypes.bool,
      priority: PropTypes.oneOf(['urgent', 'normal', 'low']),
      requires_action: PropTypes.bool,
      action_label: PropTypes.string,
      grouped_count: PropTypes.number,
      snoozable: PropTypes.bool,
      snoozed_until: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  // True totals from redux. Optional — when omitted the panel falls back
  // to a locally-derived count over the loaded slice (legacy behavior).
  trueUnreadCount: PropTypes.number,
  totalCount: PropTypes.number,
  // Infinite-scroll plumbing — all three required to be useful.
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onAction: PropTypes.func,
  onDismiss: PropTypes.func,
  onMarkRead: PropTypes.func,
  onMarkAllRead: PropTypes.func,
  onViewAll: PropTypes.func,
  onSnooze: PropTypes.func,
  onOpenSettings: PropTypes.func,
};
